import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import { compose, pure, withState } from 'recompose'
import Dropzone from 'react-dropzone'
import queryString from 'query-string'
import { get, map, isEmpty, isFunction } from 'lodash'

import { ReactComponent as PaperclipIcon } from 'assets/icons/paperclip.svg'
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg'
import { ReactComponent as PaperclipBlueIcon } from 'assets/icons/paperclip-blue.svg'
import { EN_DASH_DEFAULT_VALUE } from 'utils/constants/defaultValues'
import { getFileName } from 'utils/formatters/file'
import { formatValue } from 'utils/formatters/value'
import { uploadFiles } from 'utils/services/fileUpload'
import { Button } from 'components/button/Button'
import { Error } from 'components/error/Error'
import { ContentBlock } from 'components/contentBlock/ContentBlock'
import { FormBlock } from 'components/formBlock/FormBlock'
import { Text } from 'components/text/Text'

import styles from './UploadScan.module.scss'

const UploadScanView = ({
  files,
  onUpload,
  onRemove,
  isBusy,
  setIsBusy,
  onIsBusyChanged,
  disabled,
  title = 'Scan',
  multiple = false,
  isRequired = true,
  isReadonly = false,
  showHint = false,
  size = 'regular',
  hintText = 'File size cannot exceed 10 MB.',
  error,
  buttonText = 'Upload',
  description,
  formId,
}) => (
  <Fragment>
    {isReadonly && (
      <Fragment>
        <ContentBlock title={title}>
          <div>
            {description && <div className={styles.descriptionText}>{description}</div>}

            {!files.length && EN_DASH_DEFAULT_VALUE}
            {map(files, file => (
              <div key={get(file, 'objectStoreKey')} className={styles.readonlyLink}>
                <a href={get(file, 'fileUrl')} target="_blank" rel="noopener noreferrer" className={styles.link}>
                  <PaperclipIcon className={styles.linkIcon} width={16} height={17} />
                  {formatValue(getFileName(file))}
                </a>
              </div>
            ))}
          </div>
        </ContentBlock>
      </Fragment>
    )}

    {!isReadonly && (
      <Fragment>
        <FormBlock title={title} isRequired={isRequired} className={styles.dropzoneContainer}>
          <div className={styles.dropzoneWrapper}>
            {description && <div className={styles.descriptionText}>{description}</div>}

            <Dropzone
              multiple={multiple}
              disabled={disabled || isBusy}
              className={styles.dropzone}
              maxSize={10 * 1024 * 1024} // NOTE: skip files with size more that 10 Mb
              onDrop={files => {
                if (!isEmpty(files)) {
                  setIsBusy(true)

                  onIsBusyChanged && onIsBusyChanged(true)
                  uploadFiles(files, formId)
                    .then(uploaded => {
                      uploaded.forEach(file => {
                        const url = get(file, 'fileUrl')
                        const fileUrl = get(queryString.parseUrl(url), 'url')
                        const result = { ...file, fileUrl }

                        isFunction(onUpload) && onUpload(result)
                      })
                    })
                    .finally(() => {
                      setIsBusy(false)
                      onIsBusyChanged && onIsBusyChanged(false)
                    })
                }
              }}>
              <Button
                type="secondary"
                size="regular"
                pending={isBusy}
                disabled={disabled || isBusy}
                className={styles.button}>
                <PaperclipBlueIcon className={styles.icon} />
                <span>{buttonText}</span>
              </Button>
            </Dropzone>

            {showHint && (
              <Text size={12} lineHeight={1.33} color="greyBlue" className={styles.hint}>
                {hintText}
              </Text>
            )}
          </div>
        </FormBlock>
        <div className={styles.linkContainer}>
          {map(files, (file, index) => (
            <div key={get(file, 'objectStoreKey')} className={styles.linkWrapper}>
              <a href={get(file, 'fileUrl')} target="_blank" rel="noopener noreferrer" className={styles.link}>
                <PaperclipIcon className={styles.linkIcon} width={16} height={17} />
                {formatValue(getFileName(file))}
              </a>

              <CloseIcon
                width={12}
                height={12}
                className={styles.close}
                name="close"
                onClick={() => isFunction(onRemove) && onRemove(index + 1)}
              />
            </div>
          ))}
        </div>
      </Fragment>
    )}

    {!isBusy && <Error>{error}</Error>}
  </Fragment>
)

UploadScanView.propTypes = {
  isBusy: PropTypes.bool.isRequired,
  setIsBusy: PropTypes.func.isRequired,

  disabled: PropTypes.bool,
  files: PropTypes.array.isRequired,
  onUpload: PropTypes.func,
  onRemove: PropTypes.func,
  onIsBusyChanged: PropTypes.func,
  title: PropTypes.string,
  multiple: PropTypes.bool,
  isRequired: PropTypes.bool,
  isReadonly: PropTypes.bool,
  showHint: PropTypes.bool,
  size: PropTypes.oneOf(['regular', 'compact']),
  hintText: PropTypes.string,
  error: PropTypes.string,
  buttonText: PropTypes.string,
  description: PropTypes.node,
  formId: PropTypes.string.isRequired,
}

export const UploadScan = compose(pure, withState('isBusy', 'setIsBusy', false))(UploadScanView)

UploadScan.propTypes = {
  disabled: PropTypes.bool,
  files: PropTypes.array.isRequired,
  onUpload: PropTypes.func,
  onRemove: PropTypes.func,
  onIsBusyChanged: PropTypes.func,
  title: PropTypes.string,
  multiple: PropTypes.bool,
  isRequired: PropTypes.bool,
  isReadonly: PropTypes.bool,
  showHint: PropTypes.bool,
  size: PropTypes.oneOf(['regular', 'compact']),
  hintText: PropTypes.string,
  error: PropTypes.string,
  buttonText: PropTypes.string,
  description: PropTypes.node,
  formId: PropTypes.string.isRequired,
}
